"use client";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import CustomAlert from "@/Shared/CustomAlert/CustomAlert";

export interface AlertConfig {
  type: "success" | "error" | "warning" | "info";
  title?: string;
  message: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

interface AlertContextType {
  showAlert: (config: AlertConfig) => void;
  hideAlert: () => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    // For static generation, provide a fallback that doesn't break
    if (typeof window === "undefined") {
      console.warn("useAlert called during SSR, providing fallback");
      return {
        showAlert: () => {},
        hideAlert: () => {},
        showSuccess: () => {},
        showError: () => {},
        showWarning: () => {},
        showInfo: () => {},
      };
    }
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

/**
 * AlertProvider manages alert state and provides alert functionality to child components.
 * It enables centralized control of notifications and messages.
 */
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<AlertConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Ensure we're on the client side
  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const showAlert = useCallback((config: AlertConfig) => {
    setAlert(config);
    setIsVisible(true);
  }, []);

  const hideAlert = useCallback(() => {
    setIsVisible(false);
    // Clear alert after animation
    setTimeout(() => setAlert(null), 200);
  }, []);

  const showSuccess = useCallback(
    (message: string, title?: string) => {
      showAlert({
        type: "success",
        message,
        title,
        autoClose: true,
        autoCloseDelay: 3000,
      });
    },
    [showAlert],
  );

  const showError = useCallback(
    (message: string, title?: string) => {
      showAlert({
        type: "error",
        message,
        title,
        autoClose: true,
        autoCloseDelay: 5000,
      });
    },
    [showAlert],
  );

  const showWarning = useCallback(
    (message: string, title?: string) => {
      showAlert({
        type: "warning",
        message,
        title,
        autoClose: true,
        autoCloseDelay: 4000,
      });
    },
    [showAlert],
  );

  const showInfo = useCallback(
    (message: string, title?: string) => {
      showAlert({
        type: "info",
        message,
        title,
        autoClose: true,
        autoCloseDelay: 3000,
      });
    },
    [showAlert],
  );

  const value = {
    showAlert,
    hideAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}

      {/* Global Alert Component - only render on client */}
      {isClient && alert && (
        <CustomAlert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          isVisible={isVisible}
          onClose={hideAlert}
          autoClose={alert.autoClose}
          autoCloseDelay={alert.autoCloseDelay}
        />
      )}
    </AlertContext.Provider>
  );
};
